// Global authentication state. Holds the current user (or null) plus a
// loading flag for the initial /me probe that runs on mount.
//
// Token persistence lives in ./storage; the API client transparently injects
// the Bearer header. We listen for the "auth:expired" event the client emits
// on 401 to keep React state in sync when a token becomes invalid mid-session.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as authApi from "../api/auth";
import { ApiError, AUTH_EXPIRED_EVENT } from "../api/client";
import type { UserWithWallet } from "../api/types";
import { clearToken, getToken, setToken } from "./storage";
import i18n from "../i18n";

interface AuthContextValue {
  user: UserWithWallet | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  // refresh / refreshMe both re-fetch GET /api/me and update the cached user
  // (including the embedded wallet). refreshMe is the canonical name; refresh
  // is retained as an alias for callers that pre-date Phase 1B.
  refresh: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserWithWallet | null>(null);
  const [loading, setLoading] = useState<boolean>(() => getToken() !== null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      return;
    }
    try {
      const me = await authApi.getMe();
      if (mounted.current) {
        setUser(me);
        i18n.changeLanguage(me.locale);
      }
    } catch (err) {
      // On 401 the client clears the token + fires auth:expired; we just
      // surface that here by clearing local state. Other errors leave the
      // last known user in place to avoid flicker.
      if (err instanceof ApiError && err.status === 401) {
        if (mounted.current) setUser(null);
      } else {
        throw err;
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const token = getToken();
    if (!token) {
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const me = await authApi.getMe();
        if (!cancelled && mounted.current) {
          setUser(me);
          i18n.changeLanguage(me.locale);
        }
      } catch {
        // ApiError on 401 already cleared the token. Any other failure here
        // is treated as "session probe inconclusive" - we still clear so the
        // UI lands on /login rather than spinning forever.
        clearToken();
        if (!cancelled && mounted.current) setUser(null);
      } finally {
        if (!cancelled && mounted.current) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    function onExpired() {
      if (mounted.current) setUser(null);
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
    };
  }, []);

  // Refetch user data (including wallet balance) when the tab regains focus
  // or becomes visible again. Covers the common case of returning from an
  // external payment page (Polar checkout) where the webhook may have
  // already credited the wallet while the user was on another tab/site.
  useEffect(() => {
    let throttleTimer: ReturnType<typeof setTimeout> | null = null;
    const THROTTLE_MS = 5_000;

    function scheduleRefresh() {
      if (!getToken() || !mounted.current) return;
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
      }, THROTTLE_MS);
      refresh().catch(() => {});
    }

    function onVisibilityChange() {
      if (document.visibilityState === "visible") scheduleRefresh();
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", scheduleRefresh);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", scheduleRefresh);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await authApi.login({ email, password });
    setToken(res.access_token);
    const me = await authApi.getMe();
    if (mounted.current) {
      setUser(me);
      i18n.changeLanguage(me.locale);
    }
  }, []);

  const signup = useCallback(async (email: string, password: string) => {
    const res = await authApi.signup({ email, password });
    setToken(res.access_token);
    const me = await authApi.getMe();
    if (mounted.current) {
      setUser(me);
      i18n.changeLanguage(me.locale);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Best-effort: a server-side logout failure shouldn't trap the user in
      // a logged-in shell. We still clear local credentials below.
    }
    clearToken();
    if (mounted.current) setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, signup, logout, refresh, refreshMe: refresh }),
    [user, loading, login, signup, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
